import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import SEOHead from '../components/SEO/SEOHead';

// Icons
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LanguageIcon from '@mui/icons-material/Language';
import DevicesIcon from '@mui/icons-material/Devices';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CodeIcon from '@mui/icons-material/Code';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const About = () => {
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';

  // Dynamic Theme Palette Tokens
  const bgCanvas = isLight ? '#f8fafc' : '#0b0c0e';
  const bgSurface = isLight ? '#ffffff' : '#131418';
  const bgSurfaceElevated = isLight ? '#f1f5f9' : '#181a20';
  const bgSubtle = isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.03)';
  const textPrimary = isLight ? '#0f172a' : '#f7f7f8';
  const textSecondary = isLight ? '#475569' : '#94a3b8';
  const textBody = isLight ? '#334155' : '#cbd5e1';
  const borderCard = isLight ? '#e2e8f0' : 'rgba(247, 247, 248, 0.08)';
  const borderSubtle = isLight ? '#e2e8f0' : 'rgba(247, 247, 248, 0.06)';
  const cardShadow = isLight ? '0 4px 20px -2px rgba(0, 0, 0, 0.06), 0 2px 6px -1px rgba(0, 0, 0, 0.04)' : 'none';
  const heroGradient = isLight
    ? 'radial-gradient(circle at 50% 10%, rgba(6, 182, 212, 0.15) 0%, rgba(248, 250, 252, 0) 70%)'
    : 'radial-gradient(circle at 50% 10%, rgba(6, 182, 212, 0.12) 0%, rgba(11, 12, 14, 0) 70%)';
  const headlineGradient = isLight
    ? 'linear-gradient(135deg, #0f172a 0%, #334155 50%, #0284c7 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 50%, #38bdf8 100%)';

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About VerveAI — Mission, Vision & Team | Powered by Verve AI Labs",
    "url": "https://verveai.co/about",
    "description": "VerveAI is the dedicated startup and SMB launchpad arm of Verve AI Labs. We build turnkey Day-1 digital foundations and production-ready custom AI MVPs.",
    "publisher": {
      "@type": "Organization",
      "name": "Verve AI Labs",
      "url": "https://verveailabs.com",
      "description": "Enterprise AI Engineering, Custom MCP Servers, and Autonomous Agent Systems"
    }
  };

  return (
    <Box sx={{ bgcolor: bgCanvas, color: textPrimary, minHeight: '100vh', transition: 'background-color 0.25s ease' }}>
      <SEOHead
        title="About VerveAI — Mission, Vision & Team | Powered by Verve AI Labs"
        description="VerveAI is the startup and SMB velocity studio of Verve AI Labs. We build turnkey Day-1 digital foundations and production-ready custom AI MVPs with 100% IP ownership."
        canonical="https://verveai.co/about"
        jsonLd={schema}
      />

      {/* ============================================================== */}
      {/* HERO SECTION */}
      {/* ============================================================== */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          background: heroGradient,
          borderBottom: `1px solid ${borderSubtle}`,
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumb Navigation */}
          <Box sx={{ mb: 4 }}>
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{
                color: textSecondary,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
                '&:hover': { color: '#06b6d4', bgcolor: 'transparent' },
              }}
            >
              Back to Home
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', maxWidth: '860px', mx: 'auto' }}>
            {/* Reverse Bridge Pill Badge */}
            <Box
              component="a"
              href="https://verveailabs.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.2,
                bgcolor: isLight ? '#ecfeff' : 'rgba(6, 182, 212, 0.08)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                px: 2.2,
                py: 0.8,
                borderRadius: '999px',
                mb: 3.5,
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isLight ? '#cffafe' : 'rgba(6, 182, 212, 0.16)',
                  borderColor: 'rgba(6, 182, 212, 0.6)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
              <Typography
                variant="caption"
                sx={{
                  color: isLight ? '#0891b2' : '#e2e8f0',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                }}
              >
                Startup Velocity Arm of{' '}
                <Box
                  component="span"
                  sx={{
                    color: isLight ? '#0891b2' : '#22d3ee',
                    fontWeight: 800,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Verve AI Labs ↗
                </Box>
              </Typography>
            </Box>

            {/* Main Headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.03em',
                mb: 3,
                background: headlineGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Engineered for Founder Velocity.
              <br />
              <Box component="span" sx={{ color: '#06b6d4', WebkitTextFillColor: '#06b6d4' }}>
                Turnkey Foundations & Custom AI MVPs.
              </Box>
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: textSecondary,
                lineHeight: 1.7,
                mb: 6,
                maxWidth: '760px',
                mx: 'auto',
              }}
            >
              VerveAI (verveai.co) is the dedicated startup and SMB velocity studio of Verve AI Labs (verveailabs.com). While our parent research lab engineers sovereign enterprise agent swarms and custom MCP architectures, VerveAI equips newly incorporated founders with complete Day-1 infrastructure in 48–72 hours and production custom AI MVPs in 2–4 weeks.
            </Typography>

            {/* Key Velocity Metrics Grid */}
            <Grid container spacing={2.5} justifyContent="center">
              {[
                { number: '48–72 Hrs', label: 'Turnkey Day-1 Launch', sub: 'Domains, Web, Email & AI Receptionist' },
                { number: '2–4 Weeks', label: 'Custom AI MVP Sprints', sub: 'Production Full-Stack Applications' },
                { number: '100%', label: 'In-House Client IP', sub: 'Zero Vendor Lock-in or Recurring Rent' },
                { number: 'Zero', label: 'No-Code Spaghetti', sub: 'Containerized Microservices & Vector RAG' },
              ].map((m) => (
                <Grid item xs={6} md={3} key={m.label}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      bgcolor: bgSurface,
                      border: `1px solid ${borderCard}`,
                      boxShadow: cardShadow,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#06b6d4', fontSize: { xs: '1.6rem', md: '2rem' }, mb: 0.5 }}>
                      {m.number}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textPrimary, fontSize: '0.88rem', mb: 0.2 }}>
                      {m.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.75rem', lineHeight: 1.3, display: 'block' }}>
                      {m.sub}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* DUAL HORIZON MISSION SECTION */}
      {/* ============================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${borderSubtle}` }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7, maxWidth: '720px', mx: 'auto' }}>
            <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
              OUR DUAL-HORIZON COMMITMENT
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 2, color: textPrimary }}>
              Bridging Day-1 Viability with Next-Gen AI
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6 }}>
              Most agencies either charge exorbitant retainers for basic web setups or deliver fragile Zapier prototypes that break under production load. VerveAI delivers engineered permanence.
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="stretch">
            {/* Horizon 1: Day-1 Turnkey Launchpad */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3.5, sm: 4.5 },
                  borderRadius: '20px',
                  bgcolor: bgSurface,
                  border: '2px solid #06b6d4',
                  boxShadow: isLight ? '0 10px 30px rgba(6, 182, 212, 0.12)' : '0 0 35px rgba(6, 182, 212, 0.15)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Chip
                  label="HORIZON 1 • 48–72 HOURS"
                  size="small"
                  sx={{
                    bgcolor: isLight ? '#ecfeff' : 'rgba(6, 182, 212, 0.12)',
                    color: isLight ? '#0891b2' : '#22d3ee',
                    border: '1px solid rgba(6, 182, 212, 0.35)',
                    fontWeight: 800,
                    fontSize: '10px',
                    width: 'fit-content',
                    mb: 2,
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.6rem', mb: 1.5 }}>
                  Turnkey Day-1 Foundations
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.92rem', lineHeight: 1.6, mb: 3 }}>
                  When incorporation certificates arrive, founders lose valuable weeks coordinating domain registrars, web developers, email DNS errors, and phone receptionists. We bundle and provision everything under one turnkey roof in 48–72 hours starting at ₹4,999.
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    'Official Brand Domain (.in / .com) with Cloudflare DNS & SSL',
                    'High-Speed 5-Page Web Storefront (sub-second performance)',
                    'Custom Domain Corporate Email with SPF/DKIM/DMARC deliverability',
                    '24/7 AI WhatsApp Customer Receptionist for automated qualification',
                  ].map((feat) => (
                    <Box component="li" key={feat} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, color: textBody, fontSize: '0.88rem' }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#06b6d4', mt: 0.2, shrink: 0 }} />
                      <span>{feat}</span>
                    </Box>
                  ))}
                </Box>

                <Button
                  component={Link}
                  to="/#packages"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                    color: '#ffffff',
                    py: 1.2,
                    fontWeight: 700,
                    borderRadius: '9px',
                    boxShadow: '0 4px 14px rgba(6, 182, 212, 0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)' },
                  }}
                >
                  Explore Launchpad Packages (50% Off)
                </Button>
              </Box>
            </Grid>

            {/* Horizon 2: Custom AI MVP Development */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3.5, sm: 4.5 },
                  borderRadius: '20px',
                  bgcolor: bgSurface,
                  border: '2px solid #a855f7',
                  boxShadow: isLight ? '0 10px 30px rgba(168, 85, 247, 0.12)' : '0 0 35px rgba(168, 85, 247, 0.15)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Chip
                  label="HORIZON 2 • 2–4 WEEKS SPRINT"
                  size="small"
                  sx={{
                    bgcolor: isLight ? '#faf5ff' : 'rgba(168, 85, 247, 0.12)',
                    color: isLight ? '#7c3aed' : '#c084fc',
                    border: '1px solid rgba(168, 85, 247, 0.35)',
                    fontWeight: 800,
                    fontSize: '10px',
                    width: 'fit-content',
                    mb: 2,
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.6rem', mb: 1.5 }}>
                  Custom AI MVP Development
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.92rem', lineHeight: 1.6, mb: 3 }}>
                  When your product vision demands bespoke multi-agent logic, proprietary vector RAG pipelines, and full-stack software, no-code platforms quickly collapse. We engineer production-grade custom applications in rapid 14–28 day agile sprints with 100% IP ownership.
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    'Full-Stack Web & Mobile Applications (Next.js, React, Node, PostgreSQL)',
                    'Deterministic AI Agent Swarms replacing brittle Zapier workflows',
                    'Production Vector Search & Private LLM Reasoning Pipelines',
                    '100% In-House Intellectual Property & Private Cloud Deployment',
                  ].map((feat) => (
                    <Box component="li" key={feat} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, color: textBody, fontSize: '0.88rem' }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#a855f7', mt: 0.2, shrink: 0 }} />
                      <span>{feat}</span>
                    </Box>
                  ))}
                </Box>

                <Button
                  component="a"
                  href={`https://wa.me/919415967302?text=${encodeURIComponent('Hi VerveAI! I would like to discuss scoping a 2-4 Week Custom AI MVP.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  endIcon={<WhatsAppIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    py: 1.2,
                    fontWeight: 700,
                    borderRadius: '9px',
                    boxShadow: '0 4px 14px rgba(168, 85, 247, 0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #c084fc 0%, #6d28d9 100%)' },
                  }}
                >
                  Scope Your Custom MVP (WhatsApp)
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* THE PARENT STUDIO CONNECTION (VERVE AI LABS) */}
      {/* ============================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: bgSurfaceElevated, borderBottom: `1px solid ${borderSubtle}` }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              p: { xs: 3.5, sm: 5, md: 6 },
              borderRadius: '24px',
              bgcolor: bgSurface,
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(6, 182, 212, 0.35)',
              boxShadow: isLight ? '0 10px 40px rgba(0, 0, 0, 0.08)' : '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 50px rgba(6, 182, 212, 0.12)',
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: isLight ? '#ecfeff' : 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    px: 1.8,
                    py: 0.4,
                    borderRadius: '999px',
                    mb: 2,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 15, color: '#06b6d4' }} />
                  <Typography variant="caption" sx={{ color: isLight ? '#0891b2' : '#22d3ee', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    ENTERPRISE RESEARCH & ENGINEERING STUDIO
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, color: textPrimary, mb: 2, letterSpacing: '-0.02em' }}>
                  The Architectural Roots of Verve AI Labs
                </Typography>
                <Typography variant="body1" sx={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.7, mb: 3 }}>
                  VerveAI is powered by the foundational research, deterministic MCP protocols, and autonomous swarm orchestrations engineered at <strong>Verve AI Labs</strong> (verveailabs.com). While our parent studio architects sovereign private cloud AI, in-house SFT/RLHF alignment, and custom enterprise Model Context Protocol servers for corporations, VerveAI democratizes this capability for high-growth startups.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    component="a"
                    href="https://verveailabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      borderColor: '#06b6d4',
                      color: '#06b6d4',
                      fontWeight: 700,
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': { bgcolor: isLight ? '#ecfeff' : 'rgba(6, 182, 212, 0.08)', borderColor: '#22d3ee' },
                    }}
                  >
                    Visit Verve AI Labs (verveailabs.com) ↗
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { title: 'Deterministic Tool Calling', desc: 'Enterprise-grade Model Context Protocol (MCP) data connectors' },
                    { title: 'Zero AI Data Retention', desc: 'No client prompts or proprietary workflows are ever used for model training' },
                    { title: 'Sovereign Multi-Tenant Isolation', desc: 'Encrypted tenant boundaries matching enterprise compliance' },
                    { title: 'Zero Vendor Lock-in', desc: 'Deploy to AWS, GCP, Azure, or on-premises sovereign VPCs' },
                  ].map((item) => (
                    <Box
                      key={item.title}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        bgcolor: bgSubtle,
                        border: `1px solid ${borderSubtle}`,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textPrimary, fontSize: '0.88rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.78rem', display: 'block', mt: 0.3 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* CORE VALUES / WHY WORK WITH US */}
      {/* ============================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${borderSubtle}` }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: '720px', mx: 'auto' }}>
            <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
              FOUNDING PRINCIPLES
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 2, color: textPrimary }}>
              Why Founders Build With VerveAI
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary, fontSize: '0.95rem' }}>
              We reject agency fluff, non-technical middlemen, and fragile no-code workarounds.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <SpeedIcon sx={{ fontSize: 28, color: '#06b6d4' }} />,
                title: 'High-Velocity Turnaround',
                desc: 'Turnkey digital foundations deployed in 48–72 hours. Custom full-stack AI MVPs delivered in 2–4 week agile milestones.',
              },
              {
                icon: <CodeIcon sx={{ fontSize: 28, color: '#a855f7' }} />,
                title: '100% Client IP Ownership',
                desc: 'You receive complete source code, deployment scripts, and credentials. Zero platform taxes, licensing lock-ins, or hostage fees.',
              },
              {
                icon: <HandshakeIcon sx={{ fontSize: 28, color: '#10b981' }} />,
                title: 'Direct Lead Architect Access',
                desc: 'Work directly with senior systems engineers and AI leads. No junior trainees or non-technical account managers.',
              },
              {
                icon: <LockOutlinedIcon sx={{ fontSize: 28, color: '#f59e0b' }} />,
                title: 'Cryptographic Security & Deliverability',
                desc: 'SPF, DKIM, and DMARC DNS records configured from Day 1 so your corporate emails land in primary inboxes, not spam folders.',
              },
            ].map((val) => (
              <Grid item xs={12} sm={6} md={3} key={val.title}>
                <Card
                  sx={{
                    bgcolor: bgSurface,
                    border: `1px solid ${borderCard}`,
                    borderRadius: '16px',
                    height: '100%',
                    boxShadow: cardShadow,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: '#06b6d4',
                      transform: 'translateY(-3px)',
                      boxShadow: isLight ? '0 12px 30px rgba(6, 182, 212, 0.12)' : '0 12px 30px rgba(6, 182, 212, 0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)',
                        border: `1px solid ${borderSubtle}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {val.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: textPrimary, mb: 1 }}>
                      {val.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                      {val.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* FINAL CALL TO ACTION */}
      {/* ============================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: textPrimary, mb: 2 }}>
            Ready to Launch Your Startup Digital Footprint?
          </Typography>
          <Typography variant="body1" sx={{ color: textSecondary, fontSize: '1rem', mb: 5, maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
            Claim your 50% inaugural launch discount (Code: LAUNCH50) or schedule a direct architectural discussion with our engineering lead.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/#packages"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                color: '#ffffff',
                px: 4,
                py: 1.4,
                fontSize: '0.95rem',
                fontWeight: 700,
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Explore Packages (₹4,999) →
            </Button>

            <Button
              component="a"
              href={`https://wa.me/919415967302?text=${encodeURIComponent('Hi VerveAI! I want to discuss launching my startup infrastructure.')}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              startIcon={<WhatsAppIcon />}
              sx={{
                borderColor: 'rgba(16, 185, 129, 0.4)',
                color: isLight ? '#059669' : '#34d399',
                bgcolor: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.05)',
                px: 3.5,
                py: 1.4,
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: '10px',
                '&:hover': {
                  borderColor: '#10b981',
                  bgcolor: isLight ? '#d1fae5' : 'rgba(16, 185, 129, 0.15)',
                },
              }}
            >
              Direct WhatsApp (+91 9415967302) ↗
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
